import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@shared/store';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import {
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useUserMeControllerUserMeQuery,
  type AgentProfileResponseDto,
} from '@shared/services/apiService';
import { AddAgentProfileModal } from '@pages/Admin/Assistants/ui/components/AddAgentProfileModal';
import { DeleteAgentProfileModal } from '@pages/Admin/Assistants/ui/components/DeleteAgentProfileModal';
import { useWorkspaceJobUsageQuery } from './workspaceApi';
import { useWorkspaceScope } from './useWorkspaceScope';
import { CopyJobAction } from './CopyJobAction';
import { CreateAssistantDialog } from './CreateAssistantDialog';
import { JobAssignmentsDialog } from './JobAssignmentsDialog';
import { jobSectors, jobSectorLabel, type WorkspaceJobProfile } from './jobMetadata';

export const jobDetailsPath = (id: string) => `/workspace/jobs/${encodeURIComponent(id)}`;
export const JobsWorkspace = ({ embedded = false }: { embedded?: boolean }) => {
  const scope = useWorkspaceScope();
  return <JobsSession key={scope} scope={scope} embedded={embedded} />;
};

const JobsSession = ({ scope, embedded }: { scope: string; embedded: boolean }) => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const profiles = useAgentProfileListControllerAgentProfileListForUsersQuery(
    { filter: undefined },
    { refetchOnMountOrArgChange: true },
  );
  const usage = useWorkspaceJobUsageQuery({ scope }, { refetchOnMountOrArgChange: true });
  const me = useUserMeControllerUserMeQuery();
  const canManage =
    !profiles.isError &&
    !me.isError &&
    user?.role !== 'viewer' &&
    me.currentData?.companyId === user?.companyId &&
    !!me.currentData?.privileges.includes('agent.manage');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [sector, setSector] = useState('all');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('name');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<AgentProfileResponseDto | null>(null);
  const [using, setUsing] = useState<AgentProfileResponseDto | null>(null);
  const [assignments, setAssignments] = useState<AgentProfileResponseDto | null>(null);
  const [copy, setCopy] = useState<AgentProfileResponseDto | null>(null);
  const counts = new Map(
    (usage.isError ? [] : usage.currentData || []).map((row) => [row.profileId, row]),
  );
  const available: WorkspaceJobProfile[] = profiles.currentData || [];
  const query = search.trim().toLocaleLowerCase();
  const matching = available.filter(
    (job) =>
      (kind === 'all' || job.type === kind) &&
      (sector === 'all' || (sector === 'unspecified' ? !job.sector : job.sector === sector)) &&
      (!query ||
        `${job.name} ${job.summary || ''} ${job.prompt || ''} ${job.firstMessage || ''}`
          .toLocaleLowerCase()
          .includes(query)),
  );
  const unknown = matching.filter((job) => !counts.has(job.id)).length;
  const known = !usage.isError && !!usage.currentData && !unknown;
  const shown = matching
    .filter(
      (job) =>
        filter === 'all' ||
        (counts.has(job.id) &&
          (filter === 'used'
            ? counts.get(job.id)!.agentCount > 0
            : counts.get(job.id)!.agentCount === 0)),
    )
    .sort((a, b) => {
      if (sort === 'usage') {
        const left = counts.get(a.id)?.agentCount,
          right = counts.get(b.id)?.agentCount;
        if (left === undefined && right !== undefined) return 1;
        if (right === undefined && left !== undefined) return -1;
        if (left !== undefined && right !== undefined && left !== right) return right - left;
      }
      return a.name.localeCompare(b.name);
    });
  const loadCurrent = async (id: string) => {
    const result = await profiles.refetch();
    if (result.error) throw new Error('Unable to reload the current Job library.');
    return result.data?.find((job) => job.id === id);
  };
  if (profiles.isError)
    return (
      <Alert severity="error">
        The current Job library could not be loaded.{' '}
        <Button onClick={() => void profiles.refetch()}>Retry jobs</Button>
      </Alert>
    );
  if (!profiles.currentData) return <CircularProgress aria-label="Loading job library" />;
  return (
    <Stack component="section" aria-label="Native job library" gap={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between">
        <Box>
          <Typography component="h1" variant="h3">
            {variant === 'v2' && !embedded ? 'Find an assistant' : 'Job library'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            A job defines reusable instructions, an opening message and a workflow. Assistants use
            its saved configuration.
          </Typography>
        </Box>
        <Button variant="contained" disabled={!canManage} onClick={() => setAdding(true)}>
          New job
        </Button>
      </Stack>
      {!canManage && (
        <Alert severity="info">
          Job management permission is required for changes. Saved jobs and current assignments
          remain available for inspection.
        </Alert>
      )}
      {(usage.isError || !known) && (
        <Alert severity={usage.isError ? 'warning' : 'info'}>
          {usage.isError
            ? 'Usage counts are unavailable.'
            : usage.isFetching
              ? 'Loading current usage counts.'
              : `${unknown} matching job(s) have unknown usage.`}{' '}
          Unknown usage is never treated as unused.{' '}
          <Button onClick={() => void usage.refetch()}>Refresh usage</Button>
        </Alert>
      )}
      {copy && (
        <Alert severity="success">
          Created editable job “{copy.name}”.{' '}
          <Button onClick={() => workspace.navigate(jobDetailsPath(copy.id))}>
            Open copied job
          </Button>
        </Alert>
      )}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Tabs
          value={filter}
          onChange={(_, next) => setFilter(next)}
          aria-label="Filter jobs by usage"
          variant="scrollable"
          scrollButtons="auto">
          <Tab value="all" label={`All jobs (${matching.length})`} />
          <Tab value="used" label="In use" disabled={!known} />
          <Tab value="unused" label="Not in use" disabled={!known} />
        </Tabs>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ p: 2 }}>
          <TextField
            size="small"
            label="Search jobs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Job type"
            value={kind}
            onChange={(event) => setKind(event.target.value)}
            sx={{ minWidth: 160 }}>
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="custom">Your jobs</MenuItem>
            <MenuItem value="system">System jobs</MenuItem>
            <MenuItem value="customer-template">Templates</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Filter by sector"
            value={sector}
            onChange={(event) => setSector(event.target.value)}
            sx={{ minWidth: 180 }}>
            <MenuItem value="all">All sectors</MenuItem>
            <MenuItem value="unspecified">Unspecified</MenuItem>
            {jobSectors.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Sort jobs"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            sx={{ minWidth: 160 }}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="usage">Usage · unknown last</MenuItem>
          </TextField>
        </Stack>
        {filter !== 'all' && !known ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            This usage filter needs complete current counts. Refresh usage or choose All jobs.
          </Alert>
        ) : (
          shown.map((job) => {
            const count = counts.get(job.id);
            return (
              <Box
                component="article"
                key={job.id}
                aria-label={job.name}
                sx={{ p: { xs: 2, sm: 3 }, borderTop: 1, borderColor: 'divider' }}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  gap={2}
                  justifyContent="space-between">
                  <Stack gap={1} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Button
                        onClick={() => workspace.navigate(jobDetailsPath(job.id))}
                        sx={{ p: 0, fontWeight: 600, color: 'text.primary', textAlign: 'start' }}>
                        {job.name}
                      </Button>
                      <Chip
                        label={
                          job.type === 'customer-template'
                            ? 'Template'
                            : job.type === 'system'
                              ? 'System'
                              : 'Custom'
                        }
                        size="small"
                        variant="outlined"
                      />
                      {job.sector && (
                        <Chip label={jobSectorLabel(job.sector)} size="small" variant="outlined" />
                      )}
                    </Stack>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                      }}>
                      {job.summary ||
                        job.prompt ||
                        (job.type === 'system'
                          ? 'Managed system instructions'
                          : 'No instructions saved.')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.taskFlow?.tasks.length
                        ? `${job.taskFlow.tasks.length} workflow tasks`
                        : 'Instructions only'}
                      {job.firstMessage ? ' · Opening message' : ''}
                    </Typography>
                  </Stack>
                  <Stack gap={1} alignItems={{ lg: 'end' }}>
                    {count ? (
                      <Button onClick={() => setAssignments(job)}>
                        {count.agentCount} {count.agentCount === 1 ? 'assistant' : 'assistants'} ·{' '}
                        {count.campaignCount} {count.campaignCount === 1 ? 'campaign' : 'campaigns'}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Usage unknown
                      </Typography>
                    )}
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Button onClick={() => workspace.navigate(jobDetailsPath(job.id))}>
                        {job.type === 'custom' && canManage ? 'Edit job' : 'View job'}
                      </Button>
                      <Button disabled={!canManage} onClick={() => setUsing(job)}>
                        Use this job
                      </Button>
                      {job.type === 'custom' && (
                        <Button
                          color="error"
                          disabled={
                            !canManage || !count || count.agentCount > 0 || count.campaignCount > 0
                          }
                          onClick={() => setDeleting(job)}>
                          Delete job
                        </Button>
                      )}
                      <CopyJobAction
                        profile={job}
                        disabled={!canManage}
                        loadCurrent={() => loadCurrent(job.id)}
                        onCopied={(result) => {
                          setCopy(result);
                          void profiles.refetch();
                          void usage.refetch();
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            );
          })
        )}
        {!shown.length && (filter === 'all' || known) && (
          <Box sx={{ p: 4 }}>
            <Typography>No jobs match these filters.</Typography>
            <Button
              onClick={() => {
                setSearch('');
                setKind('all');
                setFilter('all');
              }}>
              Clear job filters
            </Button>
          </Box>
        )}
      </Paper>
      {deleting &&
        canManage &&
        available.find((job) => job.id === deleting.id)?.type === 'custom' &&
        counts.get(deleting.id)?.agentCount === 0 &&
        counts.get(deleting.id)?.campaignCount === 0 && (
          <DeleteAgentProfileModal
            open
            profile={deleting}
            onClose={() => setDeleting(null)}
            onSuccess={() => {
              setDeleting(null);
              void profiles.refetch();
              void usage.refetch();
            }}
          />
        )}
      <AddAgentProfileModal
        open={adding}
        onClose={() => setAdding(false)}
        onSuccess={() => {
          setAdding(false);
          void profiles.refetch();
          void usage.refetch();
        }}
      />
      {using && <CreateAssistantDialog open job={using} onClose={() => setUsing(null)} />}
      {assignments && (
        <JobAssignmentsDialog
          id={assignments.id}
          name={assignments.name}
          onClose={() => setAssignments(null)}
        />
      )}
    </Stack>
  );
};
